import type { GemxQlQueryResponse, GemxQlQueryVariables } from '~/core/gemxql/graphql';
import type { AllChartsDataMap, IPAChartDataPoint } from '../modules/analytic/types';
import type { IExtraCondition, IPageItemsPayload } from '~/core/gemxql/types';
import type { EAnalyticMode } from '~/modules/analytics/types';
import type { ColumnTypesMap } from '~/core/gemxql/helpers';
import type { AnalyticDateFilter } from '~/presentation/ui';

import {
  EAnalyticColumnKey,
  EAnalyticSource,
  EComparisonOperator,
  EGroupWithClause,
  EOrderDirectionType,
  EPageDimension,
  EPageField,
  EPageMetric,
  ERowReaderMode,
  ETimeDimension,
} from '~/core/gemxql/types';
import {
  createNumericRowReader,
  extractColumnTypes,
  extractQueryRows,
  getComparisonKey,
  getComparisonTotalsKey,
  getTotalsKey,
  parsePageItems,
} from '~/core/gemxql/helpers';
import { calculatePercentageChange } from '../modules/analytic/helpers';
import { formatDateLabel } from '../modules/analytic/helpers/chart';
import { DETAIL_METRIC_LIST } from '../constants/pageAnalyticV2';
import { buildGemXQlQuery } from '~/core/gemxql/builder';
import { EMetricKey } from '~/types/metric';

// ─── Types ────────────────────────────────────────────────────────────────────

interface IBuildDetailExtraConditionsParams {
  locationPath?: string;
  shopifyPageId?: string;
  isEnabledFilterByShopifyPageId?: boolean;
}

export interface IBuildDetailQueryVariablesParams extends IBuildDetailExtraConditionsParams {
  mode: EAnalyticMode;
  currentDates: AnalyticDateFilter;
  previousDates?: AnalyticDateFilter;
  isQueryPreviousData?: boolean;
  interval: ETimeDimension;
}

export type RawMetricRow = Record<string, unknown>;

export interface IPageDetailExtractedData {
  totalsRow?: RawMetricRow;
  comparisonTotalsRow?: RawMetricRow;
  pageItems?: IPageItemsPayload;
  columnTypes: ColumnTypesMap;
  allChartsDataMap: AllChartsDataMap;
}

interface IBuildIntervalChartDataParams {
  rows: RawMetricRow[];
  metricKeys: EMetricKey[];
  timeDimension: ETimeDimension;
  hasPrevious: boolean;
}

// ─── Query builder ────────────────────────────────────────────────────────────

export const buildDetailExtraConditions = ({
  locationPath,
  shopifyPageId,
  isEnabledFilterByShopifyPageId,
}: IBuildDetailExtraConditionsParams): IExtraCondition[] => {
  if (isEnabledFilterByShopifyPageId && shopifyPageId) {
    return [
      {
        operator: EComparisonOperator.EQ,
        fields: [EPageField.SHOPIFY_PAGE_ID],
        value: shopifyPageId,
      },
    ];
  }
  if (locationPath) {
    return [
      { operator: EComparisonOperator.EQ, fields: [EPageDimension.PAGE_PATH], value: locationPath },
    ];
  }
  return [];
};

export const buildDetailQueryVariables = ({
  mode,
  currentDates,
  previousDates,
  isQueryPreviousData,
  interval,
  locationPath,
  shopifyPageId,
  isEnabledFilterByShopifyPageId,
}: IBuildDetailQueryVariablesParams): GemxQlQueryVariables => {
  const timeDimension = interval;
  const compareDateRange = isQueryPreviousData ? previousDates : undefined;
  const extraConditions = buildDetailExtraConditions({
    locationPath,
    shopifyPageId,
    isEnabledFilterByShopifyPageId,
  });

  const input = buildGemXQlQuery({
    mode,
    sources: [EAnalyticSource.SESSIONS],
    metrics: DETAIL_METRIC_LIST,
    dimensions: [timeDimension],
    timeDimension,
    groupWithClause: EGroupWithClause.TOTALS,
    dateRange: currentDates,
    compareDateRange,
    filters: {},
    extraConditions,
    orderBy: { [timeDimension]: { field: timeDimension, direction: EOrderDirectionType.ASC } },
  });

  return { input };
};

const BREAKDOWN_COLUMN_KEYS = [
  EAnalyticColumnKey.VISITOR_ITEMS,
  EAnalyticColumnKey.DEVICE_ITEMS,
  EAnalyticColumnKey.TRAFFIC_SOURCE_ITEMS,
];

const extractRawTotalsRows = (
  firstRow: RawMetricRow,
  metricKeys: EMetricKey[],
  hasPrevious: boolean,
): { totalsRow: RawMetricRow; comparisonTotalsRow: RawMetricRow } => {
  const getTotals = createNumericRowReader(firstRow, ERowReaderMode.TOTALS);
  const totalsRow: RawMetricRow = {};
  const comparisonTotalsRow: RawMetricRow = {};

  metricKeys.forEach((key) => {
    totalsRow[key] = getTotals(key);
  });

  BREAKDOWN_COLUMN_KEYS.forEach((key) => {
    totalsRow[key] = firstRow[getTotalsKey(key)];
  });

  if (hasPrevious) {
    const getCompTotals = createNumericRowReader(firstRow, ERowReaderMode.COMPARISON_TOTALS);
    metricKeys.forEach((key) => {
      comparisonTotalsRow[key] = getCompTotals(key);
    });
    BREAKDOWN_COLUMN_KEYS.forEach((key) => {
      comparisonTotalsRow[key] = firstRow[getComparisonTotalsKey(key)];
    });
  }

  return { totalsRow, comparisonTotalsRow };
};

const extractPageItems = (firstRow: RawMetricRow): IPageItemsPayload | undefined => {
  const rawPageItems =
    firstRow[getTotalsKey(EPageMetric.PAGE_ITEMS)] ?? firstRow[EPageMetric.PAGE_ITEMS];
  return parsePageItems(rawPageItems) ?? undefined;
};

const buildIntervalChartData = ({
  rows,
  metricKeys,
  timeDimension,
  hasPrevious,
}: IBuildIntervalChartDataParams): AllChartsDataMap => {
  const allChartsDataMap: AllChartsDataMap = {};
  metricKeys.forEach((key) => {
    allChartsDataMap[key] = { current: [], previous: [] };
  });

  const previousDateKey = getComparisonKey(timeDimension);

  rows.forEach((row) => {
    const currentDate = row[timeDimension] ? String(row[timeDimension]) : '';
    const previousDate = row[previousDateKey] ? String(row[previousDateKey]) : '';
    const currentReader = createNumericRowReader(row, ERowReaderMode.DEFAULT);
    const previousReader = hasPrevious
      ? createNumericRowReader(row, ERowReaderMode.COMPARISON)
      : undefined;

    const currentLabel = formatDateLabel(currentDate, timeDimension);
    const previousLabel = hasPrevious ? formatDateLabel(previousDate, timeDimension) : undefined;

    metricKeys.forEach((metricKey) => {
      const value = currentReader(metricKey);
      const point: IPAChartDataPoint = {
        key: currentLabel.key,
        tooltipKey: currentLabel.tooltipKey,
        value,
      };

      if (hasPrevious && previousReader) {
        const previousValue = previousReader(metricKey);
        point.trend = {
          value: calculatePercentageChange(value, previousValue),
          trend: value > previousValue ? 'positive' : 'negative',
        };
        allChartsDataMap[metricKey].previous.push({
          key: previousLabel!.key,
          tooltipKey: previousLabel!.tooltipKey,
          value: previousValue,
        });
      }

      allChartsDataMap[metricKey].current.push(point);
    });
  });

  return allChartsDataMap;
};

export const extractPageDetailData = (data: {
  response?: GemxQlQueryResponse;
  interval: ETimeDimension;
  metricKeys: EMetricKey[];
  hasPrevious: boolean;
}): IPageDetailExtractedData => {
  const { response, interval, metricKeys, hasPrevious } = data;
  const rows = extractQueryRows(response);
  const columnTypes = extractColumnTypes(response);
  const timeDimension = interval ?? ETimeDimension.DAY;

  const allChartsDataMap = buildIntervalChartData({ rows, metricKeys, timeDimension, hasPrevious });

  if (!rows.length) return { columnTypes, allChartsDataMap };

  const firstRow = rows[0];
  const { totalsRow, comparisonTotalsRow } = extractRawTotalsRows(
    firstRow,
    metricKeys,
    hasPrevious,
  );
  const pageItems = extractPageItems(firstRow);

  return {
    totalsRow,
    comparisonTotalsRow,
    pageItems,
    columnTypes,
    allChartsDataMap,
  };
};

export const isRawMetricRowEmpty = (row?: RawMetricRow): boolean =>
  !row || !Number(row[EMetricKey.PAGE_VIEWS]);
